import { PropsWithChildren, useState } from 'react';
import { CellProps, Column, HeaderProps } from 'react-table';

import { ActionsCell } from 'app/components/Table/columns/ActionsCell';
import { CustomHeader } from 'app/components/Table/columns/CustomHeader';
import { useListView } from 'app/components/Table/core/ListViewProvider';
import { ActionTable, TYPE_MODAL } from 'app/components/Table/core/types';
import Typography from 'app/components/Typography';

import { LOGIN_TYPE } from 'app/modules/auth/core/enum';
import { ButtonAction } from 'app/modules/inventory/items/core/styles';

import { formatCustomDate } from 'app/helpers';
import { compact } from 'lodash';

import { ReactComponent as EditICon } from 'app/assets/icons/edit-icon.svg';

import StatusUserCard from '../components/StatusUserCard';
import { STATUS_USER } from './constants';
import { ActivityLog, User } from './type';

export const usersColumns = (
  type: LOGIN_TYPE,
  isPageSystemUsers?: boolean,
): ReadonlyArray<Column<User>> => {
  const actions: ActionTable[] = compact([
    {
      name: 'Edit',
      type: TYPE_MODAL.EDIT,
    },
    {
      name: 'Reset password',
      type: TYPE_MODAL.RESET_PASSWORD,
    },
  ]);

  const RFIDColumn = {
    id: 'provider',
    accessor: 'provider',
    Header: (props) => <CustomHeader tableProps={props} title="RFID" />,
    width: '15%',
  } as Column<User>;

  const statusAndActionColumn = [
    {
      id: 'status',
      accessor: 'status',
      Header: (props) => <CustomHeader tableProps={props} title="Status" />,
      Cell: ({ ...props }) => <StatusUserCard status={props.data[props.row.index].status} />,
    },
    {
      id: 'actions',
      width: '5%',
      Header: (props: PropsWithChildren<HeaderProps<any>>) => (
        <CustomHeader tableProps={props} title="" className="text-end min-w-100px" />
      ),
      Cell: ({ ...props }) => (
        <ActionsCell
          item={props.data[props.row.index]}
          actions={
            props.data[props.row.index].status === STATUS_USER.ACTIVATE
              ? actions.concat({
                  name: 'Inactivate',
                  type: TYPE_MODAL.STATUS,
                  titleModal: 'Inactivate account',
                } as ActionTable)
              : [
                  {
                    name: 'Activate',
                    type: TYPE_MODAL.STATUS,
                    titleModal: 'Activate account',
                  } as ActionTable,
                ]
          }
        />
      ),
    },
  ] as Column<User>[];

  const userColumns = [
    {
      id: 'createdAt',
      accessor: 'createdAt',
      Header: (props) => <CustomHeader tableProps={props} title="Created at" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <Typography lineHeight={26} variant="body-1">
          {formatCustomDate(props.data[props.row.index].createdAt)}
        </Typography>
      ),
    },
    {
      id: 'username',
      accessor: 'username',
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          title={type === LOGIN_TYPE.TABLET ? 'Username' : 'Email'}
        />
      ),
      Cell: ({ ...props }) => <p className="mb-0">{props.data[props.row.index].username}</p>,
      width: '20%',
    },
    {
      id: 'firstName',
      Header: (props) => <CustomHeader tableProps={props} title="First name" />,
      accessor: 'firstName',
      width: isPageSystemUsers ? '13%' : '18%',
    },
    {
      id: 'lastName',
      accessor: 'lastName',
      Header: (props) => <CustomHeader tableProps={props} title="Last name" />,
      width: isPageSystemUsers ? '13%' : '18%',
    },
    ...(isPageSystemUsers
      ? [
          {
            id: 'organizationName',
            accessor: 'organizationName',
            Header: (props) => <CustomHeader tableProps={props} title="Organization name" />,
            width: '15%',
            Cell: ({ ...props }) => (
              <p className="mb-0">
                {props.data[props.row.index].organizationName
                  ? props.data[props.row.index].organizationName
                  : 'ALL'}
              </p>
            ),
          },
        ]
      : []),
    {
      id: 'role',
      accessor: 'role',
      Header: (props) => <CustomHeader tableProps={props} title="Role" />,
      Cell: ({ ...props }) => (
        <div
          style={{
            minWidth: 200,
          }}
        >
          <p className="mb-0">{props.data[props.row.index].role?.name || ''}</p>
        </div>
      ),
      width: type === LOGIN_TYPE.WEB ? (isPageSystemUsers ? '14%' : '19%') : '15%',
    },
  ] as Column<User>[];

  if (type === LOGIN_TYPE.TABLET) {
    return userColumns.concat(RFIDColumn, statusAndActionColumn);
  }
  return userColumns.concat(statusAndActionColumn);
};

export const activitylogColumns: ReadonlyArray<Column<ActivityLog>> = [
  {
    id: 'username',
    accessor: 'username',
    Header: 'User name',
    Cell: ({ value }) => <Typography variant="body-1">{value}</Typography>,
  },
  {
    id: 'device_name',
    accessor: 'device_name',
    Header: 'Device name',
    Cell: ({ value }) => <Typography variant="body-1">{value}</Typography>,
  },
  {
    id: 'ward_name',
    accessor: 'ward_name',
    Header: 'Location name',
    Cell: ({ value }) => <Typography variant="body-1">{value}</Typography>,
  },
  {
    id: 'start_time',
    accessor: 'start_time',
    width: '10%',
    Header: (props) => <CustomHeader tableProps={props} title="Start time" />,
    Cell: ({ value }) => (
      <Typography lineHeight={26} variant="body-1">
        {formatCustomDate(value)}
      </Typography>
    ),
  },
  {
    id: 'remark',
    accessor: 'remark',
    width: '30%',
    Header: 'Remark',
    Cell: (props) => <RemarkCell {...(props as CellProps<ActivityLog, any>)} />,
  },
];

const RemarkCell: React.FC<CellProps<ActivityLog, any>> = ({ value, row, data }) => {
  const { setItemForUpdate } = useListView();
  const item = data[row.index] as ActivityLog;
  const [isExpanded, setIsExpanded] = useState(false);
  const openModal = () =>
    setItemForUpdate({
      originalData: item,
      type: value ? TYPE_MODAL.EDIT : TYPE_MODAL.ADD,
      isView: false,
      titleModal: value ? 'Edit Remark' : 'Add Remark',
    });
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        gap: 10,
      }}
      className="text-truncate"
    >
      <span
        onClick={openModal}
        style={{
          flexShrink: 0,
          width: 34,
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: !value ? '1px solid dark-yellow' : undefined,
          borderRadius: 4,
          cursor: 'pointer',
        }}
      >
        {item.remark ? (
          <ButtonAction size="sm" variant="warning">
            <EditICon />
          </ButtonAction>
        ) : (
          <ButtonAction size="sm" variant="success">
            +
          </ButtonAction>
        )}
      </span>
      <Typography
        variant="body-1"
        onClick={() => setIsExpanded((v) => !v)}
        style={{
          display: isExpanded ? 'block' : '-webkit-box',
          WebkitLineClamp: isExpanded ? undefined : 1,
          WebkitBoxOrient: isExpanded ? undefined : 'vertical',
          overflow: isExpanded ? 'visible' : 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          cursor: 'pointer',
        }}
      >
        {value || ''}
      </Typography>
    </div>
  );
};
